import { z } from 'zod';

/**
 * A theme is everything the player sees that is not a rule: names, colours, fonts, frames,
 * illustrations, board, icons and effect presets. Rules and the protocol only ever use ids.
 *
 * `ThemeManifestSchema` is the complete shape. `SkinSchema` is a deep-partial overlay that can
 * override any subset (only a board, only some card art, only fonts) and stack on top of a theme.
 */

const Color = z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'hex colour like #1a2b3c');
const AssetKey = z.string().min(1); // relative path inside the theme pack, e.g. "cards/geralt.webp"

export const FactionPaletteSchema = z.object({
  primary: Color,
  frame: Color,
  accent: Color,
});

export const ColorsSchema = z.object({
  factions: z.object({
    northern_realms: FactionPaletteSchema,
    nilfgaard: FactionPaletteSchema,
    scoiatael: FactionPaletteSchema,
    monsters: FactionPaletteSchema,
    neutral: FactionPaletteSchema,
  }),
  hero: z.object({ frame: Color, glow: Color }),
  special: z.object({ frame: Color }),
  rows: z.object({ close: Color, ranged: Color, siege: Color }),
  ui: z.object({
    background: Color,
    board: Color,
    panel: Color,
    text: Color,
    textMuted: Color,
    accent: Color,
    danger: Color,
    highlight: Color,
  }),
});

export const FontsSchema = z.object({
  display: z.string().min(1),
  body: z.string().min(1),
  numerals: z.string().min(1),
});

export const WeatherVfxSchema = z.object({
  tint: Color,
  particle: z.enum(['none', 'flakes', 'mist', 'drops']),
  density: z.number().min(0).max(1),
});

export const VfxSchema = z.object({
  frost: WeatherVfxSchema,
  fog: WeatherVfxSchema,
  rain: WeatherVfxSchema,
  cardLiftScale: z.number().min(1).max(1.5),
  cardTiltRadians: z.number().min(0).max(0.6),
  hoverLiftPx: z.number().min(0).max(80),
});

export const AssetsSchema = z.object({
  board: AssetKey.optional(),
  cardBack: AssetKey.optional(),
  frames: z.record(z.string(), AssetKey).default({}), // by faction | "hero" | "special" | "leader"
  rowIcons: z.record(z.string(), AssetKey).default({}), // by row
  abilityIcons: z.record(z.string(), AssetKey).default({}), // by ability | special kind
  weather: z.record(z.string(), AssetKey).default({}), // frost | fog | rain
  cards: z.record(z.string(), AssetKey).default({}), // by card id -> illustration
});

export const ThemeManifestSchema = z.object({
  id: z.string().regex(/^[a-z0-9_-]+$/),
  name: z.string().min(1),
  version: z.number().int().min(1),
  /** Theme id this one refines; missing values fall back to it, then to the built-in defaults. */
  extends: z.string().optional(),
  colors: ColorsSchema,
  fonts: FontsSchema,
  vfx: VfxSchema,
  assets: AssetsSchema,
});
export type ThemeManifest = z.infer<typeof ThemeManifestSchema>;

export const StringsSchema = z.object({
  factions: z.record(z.string(), z.string()),
  rows: z.record(z.string(), z.string()),
  abilities: z.record(z.string(), z.object({ name: z.string(), description: z.string() })),
  specials: z.record(z.string(), z.object({ name: z.string(), description: z.string() })),
  leaderAbilities: z.record(z.string(), z.string()),
  cards: z.record(z.string(), z.object({ name: z.string(), flavor: z.string().optional() })),
  ui: z.record(z.string(), z.string()),
});
export type Strings = z.infer<typeof StringsSchema>;

/**
 * Skin = deep-partial overlay. Zod 4 has no deepPartial, so the partial shapes are spelled out;
 * keep them in sync with the full schemas above (the theme test checks a round trip).
 */
const FactionPaletteSkin = FactionPaletteSchema.partial();
export const ColorsSkinSchema = z
  .object({
    factions: z
      .object({
        northern_realms: FactionPaletteSkin,
        nilfgaard: FactionPaletteSkin,
        scoiatael: FactionPaletteSkin,
        monsters: FactionPaletteSkin,
        neutral: FactionPaletteSkin,
      })
      .partial(),
    hero: ColorsSchema.shape.hero.partial(),
    special: ColorsSchema.shape.special.partial(),
    rows: ColorsSchema.shape.rows.partial(),
    ui: ColorsSchema.shape.ui.partial(),
  })
  .partial();

export const VfxSkinSchema = z
  .object({
    frost: WeatherVfxSchema.partial(),
    fog: WeatherVfxSchema.partial(),
    rain: WeatherVfxSchema.partial(),
    cardLiftScale: VfxSchema.shape.cardLiftScale,
    cardTiltRadians: VfxSchema.shape.cardTiltRadians,
    hoverLiftPx: VfxSchema.shape.hoverLiftPx,
  })
  .partial();

const NamedSkin = z.object({ name: z.string(), description: z.string() }).partial();
export const StringsSkinSchema = z
  .object({
    factions: z.record(z.string(), z.string()),
    rows: z.record(z.string(), z.string()),
    abilities: z.record(z.string(), NamedSkin),
    specials: z.record(z.string(), NamedSkin),
    leaderAbilities: z.record(z.string(), z.string()),
    cards: z.record(z.string(), z.object({ name: z.string(), flavor: z.string().optional() }).partial()),
    ui: z.record(z.string(), z.string()),
  })
  .partial();

/** A skin overrides any subset of a theme. Record values merge key by key; scalars replace. */
export const SkinSchema = z.object({
  id: z.string().regex(/^[a-z0-9_-]+$/),
  name: z.string().min(1),
  version: z.number().int().min(1),
  colors: ColorsSkinSchema.optional(),
  fonts: FontsSchema.partial().optional(),
  vfx: VfxSkinSchema.optional(),
  assets: AssetsSchema.partial().optional(),
  strings: StringsSkinSchema.optional(),
});
export type Skin = z.infer<typeof SkinSchema>;
